/**
 * What a link points at, so the in-app viewer can size itself to it.
 *
 * Decided from the URL alone - no request is made to find out. That keeps the
 * click instant and sends nothing to a third party before the student chooses
 * to open the page.
 */
export type LinkKind = "image" | "youtube" | "pdf" | "page";

export interface ClassifiedLink {
    kind: LinkKind;
    /** The address to put in the viewer (an embed URL for YouTube). */
    viewUrl: string;
    /** The original address, for "Open in new tab". */
    url: string;
    /** Short label for the viewer's title bar: the host, or the file name. */
    label: string;
}

const IMAGE = /\.(png|jpe?g|gif|webp|avif|svg|bmp)$/i;
const PDF = /\.pdf$/i;

/** Only http(s) is ever opened. A `javascript:` or `data:` URL is refused here,
 *  so no answer text can smuggle script into a click. */
export const safeUrl = (raw: string): URL | null => {
    try {
        const url = new URL(raw);
        return url.protocol === "https:" || url.protocol === "http:" ? url : null;
    } catch {
        return null;
    }
};

const youtubeId = (url: URL): string | null => {
    const host = url.hostname.replace(/^www\.|^m\./, "");
    if (host === "youtu.be") return url.pathname.slice(1).split("/")[0] || null;
    if (host === "youtube.com" || host === "youtube-nocookie.com") {
        if (url.pathname === "/watch") return url.searchParams.get("v");
        const match = url.pathname.match(/^\/(?:embed|shorts|live)\/([\w-]{6,})/);
        return match ? match[1] : null;
    }
    return null;
};

export const classifyLink = (raw: string): ClassifiedLink | null => {
    const url = safeUrl(raw);
    if (!url) return null;
    const host = url.hostname.replace(/^www\./, "");
    const file = decodeURIComponent(url.pathname.split("/").pop() || "");

    const video = youtubeId(url);
    if (video && /^[\w-]{6,20}$/.test(video)) {
        // youtube-nocookie is the host the site's CSP already allows for frames.
        return { kind: "youtube", viewUrl: `https://www.youtube-nocookie.com/embed/${video}`, url: url.href, label: "YouTube" };
    }
    if (IMAGE.test(url.pathname)) return { kind: "image", viewUrl: url.href, url: url.href, label: file || host };
    if (PDF.test(url.pathname)) return { kind: "pdf", viewUrl: url.href, url: url.href, label: file || host };
    return { kind: "page", viewUrl: url.href, url: url.href, label: host };
};

/** Characters that end a sentence rather than a URL: "see https://x.org." */
const TRAILING = /[.,;:!?'")\]]+$/;

/** Split a bare URL from punctuation stuck to its end. Brackets are kept when
 *  the URL itself opened one, as in a Wikipedia link like `Force_(physics)`. */
export const trimUrl = (raw: string): { url: string; rest: string } => {
    let url = raw;
    let rest = "";
    const match = url.match(TRAILING);
    if (match) {
        rest = match[0];
        url = url.slice(0, -rest.length);
        while (rest.startsWith(")") && (url.match(/\(/g) || []).length > (url.match(/\)/g) || []).length) {
            url += ")";
            rest = rest.slice(1);
        }
    }
    return { url, rest };
};
